import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import AdsClickIcon from "@mui/icons-material/Campaign";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaidIcon from "@mui/icons-material/Paid";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PercentIcon from "@mui/icons-material/Percent";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import { tokens } from "../../theme";
import { ADS_API_BASE_URL } from "../../config/api";
import Header from "../../components/Header";
import AdsLineChart from "../../components/AdsLineChart";

const RANGE_OPTIONS = [
  { label: "7D", days: 7 },
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
];

const formatNumber = (n) => Math.round(n || 0).toLocaleString();
const formatMoney = (n, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n || 0);

const StatCard = ({ icon, value, label }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box
      gridColumn="span 2"
      backgroundColor={colors.primary[400]}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      p="20px"
    >
      {icon}
      <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mt="8px">
        {value}
      </Typography>
      <Typography variant="h6" color={colors.greenAccent[500]}>
        {label}
      </Typography>
    </Box>
  );
};

const AdsDashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [days, setDays] = useState(30);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem("authToken");

        const response = await fetch(`${ADS_API_BASE_URL}/ads/metrics?days=${days}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch Google Ads metrics (${response.status})`);
        }

        const data = await response.json();
        if (!cancelled) setMetrics(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
        console.error("Google Ads metrics fetch error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMetrics();
    return () => {
      cancelled = true;
    };
  }, [days]);

  const currency = metrics?.account?.currencyCode || "USD";

  const lineData = useMemo(() => {
    if (!metrics?.daily?.length) return [];
    return [
      {
        id: "Clicks",
        color: colors.blueAccent[500],
        data: metrics.daily.map((d) => ({ x: d.date, y: d.clicks })),
      },
      {
        id: "Cost",
        color: colors.greenAccent[500],
        data: metrics.daily.map((d) => ({ x: d.date, y: Number(d.cost.toFixed(2)) })),
      },
    ];
  }, [metrics, colors]);

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="10px">
        <Header
          title="GOOGLE ADS"
          subtitle={
            metrics?.account?.name
              ? `PPC performance — ${metrics.account.name}`
              : "PPC performance overview"
          }
        />
        <ButtonGroup>
          {RANGE_OPTIONS.map((opt) => (
            <Button
              key={opt.days}
              onClick={() => setDays(opt.days)}
              sx={{
                backgroundColor:
                  days === opt.days ? colors.blueAccent[700] : colors.primary[400],
                color: colors.grey[100],
                fontWeight: "bold",
              }}
            >
              {opt.label}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" p="60px">
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Box
          backgroundColor={colors.primary[400]}
          p="20px"
          mt="20px"
          borderLeft={`4px solid ${colors.redAccent[500]}`}
        >
          <Typography color={colors.redAccent[500]} fontWeight="bold">
            Couldn't load Google Ads data
          </Typography>
          <Typography color={colors.grey[100]} mt="8px">
            {error}
          </Typography>
          <Typography color={colors.grey[300]} mt="8px" variant="body2">
            Make sure the Google Ads proxy server is running and configured — see
            server/README.md.
          </Typography>
        </Box>
      )}

      {!loading && !error && metrics && (
        <>
          <Box
            display="grid"
            gridTemplateColumns="repeat(12, 1fr)"
            gridAutoRows="120px"
            gap="20px"
            mt="20px"
          >
            <StatCard
              icon={<AdsClickIcon sx={{ color: colors.blueAccent[500], fontSize: "26px" }} />}
              value={formatNumber(metrics.summary.clicks)}
              label="Clicks"
            />
            <StatCard
              icon={<VisibilityIcon sx={{ color: colors.blueAccent[500], fontSize: "26px" }} />}
              value={formatNumber(metrics.summary.impressions)}
              label="Impressions"
            />
            <StatCard
              icon={<PaidIcon sx={{ color: colors.redAccent[400], fontSize: "26px" }} />}
              value={formatMoney(metrics.summary.cost, currency)}
              label="Cost"
            />
            <StatCard
              icon={<CheckCircleOutlineIcon sx={{ color: colors.greenAccent[500], fontSize: "26px" }} />}
              value={formatNumber(metrics.summary.conversions)}
              label="Conversions"
            />
            <StatCard
              icon={<PercentIcon sx={{ color: colors.greenAccent[500], fontSize: "26px" }} />}
              value={`${metrics.summary.ctr.toFixed(2)}%`}
              label="CTR"
            />
            <StatCard
              icon={<PointOfSaleIcon sx={{ color: colors.greenAccent[500], fontSize: "26px" }} />}
              value={formatMoney(metrics.summary.avgCpc, currency)}
              label="Avg. CPC"
            />
          </Box>

          <Box
            display="grid"
            gridTemplateColumns="repeat(12, 1fr)"
            gridAutoRows="140px"
            gap="20px"
            mt="20px"
          >
            <Box gridColumn="span 8" gridRow="span 2" backgroundColor={colors.primary[400]}>
              <Typography variant="h5" fontWeight="600" sx={{ p: "20px 30px 0 30px" }}>
                Clicks & Cost Trend
              </Typography>
              <Box height="230px" m="-10px 0 0 0">
                {lineData.length > 0 ? (
                  <AdsLineChart data={lineData} isDashboard />
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography color={colors.grey[300]}>No data for this range yet.</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              gridColumn="span 4"
              gridRow="span 2"
              backgroundColor={colors.primary[400]}
              overflow="auto"
            >
              <Box borderBottom={`4px solid ${colors.primary[500]}`} p="15px">
                <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
                  Campaigns ({metrics.campaigns.length})
                </Typography>
              </Box>
              {metrics.campaigns.length === 0 && (
                <Typography color={colors.grey[300]} p="15px">
                  No campaign activity in this range.
                </Typography>
              )}
              {metrics.campaigns.map((c) => (
                <Box
                  key={c.id}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  borderBottom={`4px solid ${colors.primary[500]}`}
                  p="15px"
                >
                  <Box>
                    <Typography color={colors.grey[100]} fontWeight="600">
                      {c.name}
                    </Typography>
                    <Typography color={colors.grey[300]} variant="body2">
                      {formatNumber(c.clicks)} clicks · {formatNumber(c.conversions)} conv.
                    </Typography>
                  </Box>
                  <Box
                    backgroundColor={colors.greenAccent[500]}
                    p="5px 10px"
                    borderRadius="4px"
                    whiteSpace="nowrap"
                  >
                    {formatMoney(c.cost, currency)}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AdsDashboard;
